import { useEffect, useRef, useState } from 'react';
import { Launcher } from './components/Launcher';
import { ChatPanel, type ChatMessage } from './components/ChatPanel';
import { Greeting } from './components/Greeting';
import { WidgetSessionExpiredError, type WidgetApiClient, type WidgetPublicConfig } from './api';
import { parseAgentResponse, type AgentResponseSegment } from './ui-components';
import {
  clearStoredSession,
  hasRecentAutoOpenDismissal,
  loadStoredSession,
  saveAutoOpenDismissal,
  saveStoredSession,
} from './session-storage';

// Mirrors the widget's own mobile breakpoint (styles.css) rather than the
// dashboard app's separate 768px breakpoint (src/hooks/use-mobile.ts) —
// that hook isn't part of this standalone bundle, and "mobile" should mean
// the same viewport width the widget already treats as mobile elsewhere.
const MOBILE_MEDIA_QUERY = '(max-width: 640px)';

interface AppProps {
  api: WidgetApiClient;
  publicId: string;
  getPageContext?: () => Record<string, unknown>;
}

export function App({ api, publicId, getPageContext }: AppProps): React.JSX.Element | null {
  const [config, setConfig] = useState<WidgetPublicConfig | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [greetingDismissed, setGreetingDismissed] = useState(false);
  const autoOpenTimerRef = useRef<number | null>(null);
  const hydratedRef = useRef(false);
  // True whenever ANY prior session was resumed on this page load, open or
  // closed, OR a recent no-message dismissal was recorded (#247) — either
  // way the visitor already made a deliberate choice about the panel, so
  // auto-open must not override it (#131 review finding).
  const hydratedSessionRef = useRef(false);
  // Tracks true last-activity time, independent of the persistence effect's
  // save cadence. Hydration restores this from storage rather than resetting
  // it to now, so simply loading a page never slides the client-side TTL
  // window forward — only an actual completed message exchange does
  // (#131 review finding: the client TTL must mirror the server's, which
  // only advances on a real message send).
  const lastActivityAtRef = useRef<number>(Date.now());
  // True when this page's context hasn't been sent to Retell yet for the
  // current session (#142) — set on hydrating a resumed session (its
  // dynamic variables were set at createChat time, possibly on a different
  // page), cleared once the first message on this page has included a
  // refresh. A brand-new session never sets this — createChat already got
  // the current page's context, so no redundant refresh is needed.
  const needsContextRefreshRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getConfig()
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch((err) => {
        console.error('[Audientic] failed to load widget config', err);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Resume a prior session on this same widget from before the visitor
  // navigated (#131). Restoring state here never talks to the network —
  // it only rehydrates local state, preserving the lazy-session guarantee
  // that a mere page view never creates a Retell chat (see #125).
  useEffect(() => {
    if (!config || hydratedRef.current) return;
    hydratedRef.current = true;

    // A visitor who closed an auto-opened panel without ever messaging
    // leaves no session behind, but has still made a deliberate choice
    // about the panel (#247) — suppress auto-open the same as a resumed
    // session would, without waiting on it here since there's no session
    // state to restore.
    if (hasRecentAutoOpenDismissal(publicId)) {
      hydratedSessionRef.current = true;
    }

    const stored = loadStoredSession(publicId, config.sessionPersistenceTtlSeconds);
    if (!stored) return;

    hydratedSessionRef.current = true;
    needsContextRefreshRef.current = true;
    lastActivityAtRef.current = stored.updatedAt;
    setSessionId(stored.sessionId);
    setMessages(stored.messages);
    setIsOpen(stored.isOpen);
    setGreetingDismissed(stored.greetingDismissed);
  }, [config, publicId]);

  // Persist session state for resume across page navigation (#131). Only
  // once a real session exists — pre-session UI state (panel opened but
  // nothing sent yet) isn't part of the "session" this feature resumes.
  useEffect(() => {
    if (sessionId === null) return;
    saveStoredSession(publicId, {
      sessionId,
      messages,
      isOpen,
      greetingDismissed,
      updatedAt: lastActivityAtRef.current,
    });
  }, [publicId, sessionId, messages, isOpen, greetingDismissed]);

  useEffect(() => {
    if (!config?.autoOpenEnabled) return;
    if (hydratedSessionRef.current) return;

    // A page-type-specific delay (#237) takes precedence over the default
    // when the current page matches one of the configured overrides —
    // resolved once here, at schedule time, since (unlike the mobile check
    // below) there's nothing to re-check once the wait is already
    // underway: the duration was fixed the moment the timer started.
    const pageType = getPageContext?.().pageType;
    const overrideDelay =
      typeof pageType === 'string' ? config.autoOpenDelayOverrides?.[pageType] : undefined;
    const delaySeconds = overrideDelay ?? config.autoOpenDelaySeconds;
    if (!delaySeconds || delaySeconds <= 0) return;

    autoOpenTimerRef.current = window.setTimeout(() => {
      autoOpenTimerRef.current = null;
      // Re-checked at fire time, not just when the timer was scheduled — the
      // viewport can cross the mobile breakpoint (resize, rotation) during
      // the delay, and a stale schedule-time check would still pop the
      // widget open on what is now a mobile viewport.
      if (config.autoOpenDisabledOnMobile && window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
      void handleOpen();
    }, delaySeconds * 1000);

    return () => {
      if (autoOpenTimerRef.current !== null) {
        window.clearTimeout(autoOpenTimerRef.current);
        autoOpenTimerRef.current = null;
      }
    };
    // handleOpen is stable enough for this single-fire timer; we deliberately
    // depend only on config so the timer is scheduled once per widget load.
    // getPageContext is likewise a stable module-level function in the real
    // widget entry point (src/widget/index.tsx) and doesn't need to retrigger
    // this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  function handleOpen(): void {
    if (autoOpenTimerRef.current !== null) {
      window.clearTimeout(autoOpenTimerRef.current);
      autoOpenTimerRef.current = null;
    }
    setIsOpen(true);
    fireEvent('widget_opened');
    if (config?.welcomeMessage) {
      setMessages((prev) => {
        if (prev.length > 0) return prev;
        return [{ id: 'welcome', role: 'agent', content: config.welcomeMessage! }];
      });
    }
  }

  function handleClose(): void {
    // No session yet means the visitor never sent a message — the only
    // record of this dismissal, so auto-open doesn't re-fire on their next
    // page (#247). Once a session exists, its own resume/TTL already
    // covers suppression, so there's nothing extra to persist here.
    if (sessionId === null) {
      saveAutoOpenDismissal(publicId);
    }
    setIsOpen(false);
    fireEvent('widget_closed');
  }

  function fireEvent(eventType: 'widget_opened' | 'widget_closed'): void {
    void api
      .recordEvent({
        eventType,
        ...(sessionId !== null ? { sessionId } : {}),
      })
      .catch((err) => console.warn(`[Audientic] ${eventType} event failed`, err));
  }

  async function handleSend(text: string): Promise<void> {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: 'user', content: text },
    ]);
    setSending(true);

    // Lazily create the Retell session on the first message so that
    // auto-open and idle page views don't create ghost sessions.
    let activeSessionId = sessionId;
    if (!activeSessionId) {
      try {
        const result = await api.startSession(getPageContext?.());
        activeSessionId = result.sessionId;
        setSessionId(result.sessionId);
      } catch (err) {
        console.error('[Audientic] failed to start widget session', err);
        appendErrorMessage();
        setSending(false);
        return;
      }
    }

    const shouldRefreshContext = needsContextRefreshRef.current;

    try {
      const result = await api.sendMessage({
        sessionId: activeSessionId,
        message: text,
        ...(shouldRefreshContext ? { customerContext: getPageContext?.() } : {}),
      });
      needsContextRefreshRef.current = false;
      appendAgentReply(result.content);
    } catch (err) {
      if (err instanceof WidgetSessionExpiredError) {
        await retryWithFreshSession(text);
      } else {
        console.error('[Audientic] failed to send message', err);
        appendErrorMessage();
      }
    } finally {
      setSending(false);
    }
  }

  // A resumed (or merely stale) session can be dead on the server — TTL
  // expired (#130) or unknown entirely. Rather than surface an error for
  // something the visitor can't act on, start a fresh session and resend
  // their message once, transparently (#131). The fresh chat has no memory
  // of anything before this point, so the visible transcript is reset to
  // just the message that triggered the retry — carrying the old transcript
  // forward would misleadingly graft a context-less reply onto conversation
  // history the new session never saw (#131 review finding).
  async function retryWithFreshSession(text: string): Promise<void> {
    clearStoredSession(publicId);
    setSessionId(null);
    setMessages([{ id: crypto.randomUUID(), role: 'user', content: text }]);
    try {
      const { sessionId: freshSessionId } = await api.startSession(getPageContext?.());
      // The fresh chat's createChat call just sent the current page's
      // context — no refresh needed on its first message, even if the
      // flag was set going into this retry.
      needsContextRefreshRef.current = false;
      setSessionId(freshSessionId);
      const result = await api.sendMessage({ sessionId: freshSessionId, message: text });
      appendAgentReply(result.content);
    } catch (err) {
      console.error('[Audientic] failed to resume with a fresh session', err);
      appendErrorMessage();
    }
  }

  function appendAgentReply(rawContent: string): void {
    lastActivityAtRef.current = Date.now();
    const segments = parseAgentResponse(rawContent);
    const hasComponent = segments.some((s) => s.type === 'component');
    const isTextSegment = (s: AgentResponseSegment): s is Extract<AgentResponseSegment, { type: 'text' }> =>
      s.type === 'text';
    const content = segments
      .filter(isTextSegment)
      .map((s) => s.value)
      .join('\n\n');
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: 'agent', content, ...(hasComponent ? { segments } : {}) },
    ]);
  }

  function appendErrorMessage(): void {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: 'agent', content: '_Sorry, something went wrong. Please try again._' },
    ]);
  }

  if (!config) return null;

  const rootStyle = {
    '--widget-primary': config.primaryColor,
  } as React.CSSProperties;

  const showGreeting =
    !isOpen && !greetingDismissed && Boolean(config.greetingMessage);

  return (
    <div className={`widget-root position-${config.position}`} style={rootStyle}>
      <div className="widget-launcher-stack">
        {isOpen && (
          <ChatPanel
            title={config.title}
            agentName={config.agentName}
            logoUrl={config.logoUrl}
            messages={messages}
            sending={sending}
            canSend={true}
            onSend={handleSend}
            onClose={handleClose}
          />
        )}
        {!isOpen && showGreeting && config.greetingMessage && (
          <Greeting
            message={config.greetingMessage}
            onDismiss={() => setGreetingDismissed(true)}
          />
        )}
        <Launcher onClick={isOpen ? handleClose : handleOpen} />
      </div>
    </div>
  );
}
